//#region IMPORTS
import { flexRender, Table } from "@tanstack/react-table";
import classNames from "../../helpers/classNames";
import MainSortableTableHeaderSortIcon from "./MainSortableTableHeaderSortIcon";
//#endregion

//#region INTERFACE
interface Props {
  changeHoverBGColor?: boolean;
  truncateHeader?: boolean;
  disabled?: boolean;
  loading: boolean;
  table: Table<any>;
}
//#endregion
export default function MainSortableTable({
  changeHoverBGColor = true,
  truncateHeader = true,
  disabled = false,
  loading,
  table,
}: Props) {
  //#region RENDER
  return (
    <div
      className={classNames(
        loading || disabled ? "opacity-50 pointer-events" : "opacity-100"
      )}
    >
      <table className="min-w-full mb-16">
        <thead className="sticky top-72 bg-white h-16 z-30">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className={classNames(
                    truncateHeader ? "truncate" : "",
                    "py-2 text-left text-xs font-semibold text-slate-800"
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      className={classNames("flex items-center gap-1")}
                      title={
                        header.column.getCanSort()
                          ? header.column.getNextSortingOrder() === "asc"
                            ? "Sort ascending"
                            : header.column.getNextSortingOrder() === "desc"
                            ? "Sort descending"
                            : "Clear sort"
                          : undefined
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort()
                        ? {
                            asc: (
                              <MainSortableTableHeaderSortIcon
                                order="asc"
                                clickHandler={() => {
                                  if (!header.column.getCanSort()) return;
                                  header.column.toggleSorting();
                                }}
                              />
                            ),
                            desc: (
                              <MainSortableTableHeaderSortIcon
                                order="desc"
                                clickHandler={() => {
                                  if (!header.column.getCanSort()) return;
                                  header.column.toggleSorting();
                                }}
                              />
                            ),
                            false: (
                              <MainSortableTableHeaderSortIcon
                                order="none"
                                clickHandler={() => {
                                  if (!header.column.getCanSort()) return;
                                  header.column.toggleSorting();
                                }}
                              />
                            ),
                          }[header.column.getIsSorted() as string] ?? null
                        : null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.original.id}
              className={classNames(
                changeHoverBGColor ? "hover:bg-gray-50" : "",
                disabled ? "cursor-not-allowed" : "hover:text-gray-950 ",
                "text-gray-500  transition-colors duration-100 ease-in-out"
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td className="text-xs py-2" key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  //#endregion
}
